import { createDefaultMap } from '../../GameWorld/tiles';

function read(source, camelName, pascalName) {
  return source?.[camelName] ?? source?.[pascalName];
}

function integerOrNull(value) {
  if (value === '' || value === null || value === undefined) return null;
  const number = Number(value);
  return Number.isInteger(number) ? number : null;
}

function stringList(value) {
  return (Array.isArray(value) ? value : [])
    .map(entry => String(entry || '').trim())
    .filter(Boolean);
}

export function normalizeRegionExit(value, index = 0) {
  return {
    key: String(read(value, 'key', 'Key') || `exit_${index + 1}`),
    label: String(read(value, 'label', 'Label') || ''),
    x: Number(read(value, 'x', 'X')) || 0,
    y: Number(read(value, 'y', 'Y')) || 0,
    direction: String(
      read(value, 'direction', 'Direction') || '',
    ).toLowerCase(),
    targetRegionId: String(
      read(value, 'targetRegionId', 'TargetRegionId') || '',
    ),
    targetExitKey: String(
      read(value, 'targetExitKey', 'TargetExitKey') || '',
    ),
    requiredRunUnlockKey: String(
      read(value, 'requiredRunUnlockKey', 'RequiredRunUnlockKey') || '',
    ),
    tollItemId: String(
      read(value, 'tollItemId', 'TollItemId') || '',
    ),
    tollQuantity: Math.max(
      0,
      Number(read(value, 'tollQuantity', 'TollQuantity')) || 0,
    ),
    tollPaidRunUnlockKey: String(
      read(value, 'tollPaidRunUnlockKey', 'TollPaidRunUnlockKey') || '',
    ),
    enabled: read(value, 'enabled', 'Enabled') !== false,
  };
}

export function normalizeFixedEncounter(value) {
  if (!value) return null;
  return {
    placementId: String(
      read(value, 'placementId', 'PlacementId') || 'fixed-encounter',
    ),
    enemyId: String(read(value, 'enemyId', 'EnemyId') || ''),
    x: Number(read(value, 'x', 'X')) || 0,
    y: Number(read(value, 'y', 'Y')) || 0,
    runUnlockKey: String(
      read(value, 'runUnlockKey', 'RunUnlockKey') || '',
    ),
    repeatAfterCooldown: Boolean(read(
      value,
      'repeatAfterCooldown',
      'RepeatAfterCooldown',
    )),
    rewardCooldownSeconds: Math.max(
      0,
      Number(read(
        value,
        'rewardCooldownSeconds',
        'RewardCooldownSeconds',
      )) || 0,
    ),
    guaranteedDropCount: Math.max(
      0,
      Number(read(
        value,
        'guaranteedDropCount',
        'GuaranteedDropCount',
      )) || 0,
    ),
    bonusDropChancePercent: Math.max(
      0,
      Math.min(100, Number(read(
        value,
        'bonusDropChancePercent',
        'BonusDropChancePercent',
      )) || 0),
    ),
  };
}

export function normalizeResourcePlacement(value, index = 0) {
  return {
    placementId: String(
      read(value, 'placementId', 'PlacementId') || `placement_${index + 1}`,
    ),
    itemId: String(read(value, 'itemId', 'ItemId') || ''),
    minimumStack: Math.max(
      1,
      Number(read(value, 'minimumStack', 'MinimumStack')) || 1,
    ),
    maximumStack: Math.max(
      1,
      Number(read(value, 'maximumStack', 'MaximumStack')) || 1,
    ),
    maximumCharges: Math.max(
      1,
      Number(read(value, 'maximumCharges', 'MaximumCharges')) || 1,
    ),
    respawnSeconds: Math.max(
      1,
      Number(read(value, 'respawnSeconds', 'RespawnSeconds')) || 60,
    ),
    x: Number(read(value, 'x', 'X')) || 0,
    y: Number(read(value, 'y', 'Y')) || 0,
    playerScoped: Boolean(read(value, 'playerScoped', 'PlayerScoped')),
    requiredRunUnlockKey: String(
      read(value, 'requiredRunUnlockKey', 'RequiredRunUnlockKey') || '',
    ),
  };
}

export function normalizeResourceSpawnRule(value, index = 0) {
  const nearValue = read(value, 'near', 'Near');
  const near = nearValue || {};
  return {
    key: String(read(value, 'key', 'Key') || `rule_${index + 1}`),
    itemId: String(read(value, 'itemId', 'ItemId') || ''),
    minimumStack: Math.max(
      1,
      Number(read(value, 'minimumStack', 'MinimumStack')) || 1,
    ),
    maximumStack: Math.max(
      1,
      Number(read(value, 'maximumStack', 'MaximumStack')) || 1,
    ),
    maximumCharges: Math.max(
      1,
      Number(read(value, 'maximumCharges', 'MaximumCharges')) || 1,
    ),
    respawnSeconds: Math.max(
      1,
      Number(read(value, 'respawnSeconds', 'RespawnSeconds')) || 60,
    ),
    count: Math.max(1, Number(read(value, 'count', 'Count')) || 1),
    allowedBaseKeys: stringList(
      read(value, 'allowedBaseKeys', 'AllowedBaseKeys'),
    ),
    allowedDetailKeysAny: stringList(
      read(value, 'allowedDetailKeysAny', 'AllowedDetailKeysAny'),
    ),
    near: nearValue == null
      ? null
      : {
        baseKeys: stringList(read(near, 'baseKeys', 'BaseKeys')),
        detailKeysAny: stringList(
          read(near, 'detailKeysAny', 'DetailKeysAny'),
        ),
        maxManhattanDistance: Math.max(
          1,
          Number(
            read(near, 'maxManhattanDistance', 'MaxManhattanDistance'),
          ) || 1,
        ),
      },
    minimumSpacing: Math.max(
      0,
      Number(read(value, 'minimumSpacing', 'MinimumSpacing')) || 0,
    ),
    excludeSpawnRadius: Math.max(
      0,
      Number(read(value, 'excludeSpawnRadius', 'ExcludeSpawnRadius')) || 0,
    ),
    rotationCycleSeconds: Math.max(
      1,
      Number(
        read(value, 'rotationCycleSeconds', 'RotationCycleSeconds'),
      ) || 86400,
    ),
    enabled: read(value, 'enabled', 'Enabled') !== false,
  };
}

export function normalizeLootContainerPlacement(value, index = 0) {
  return {
    placementId: String(
      read(value, 'placementId', 'PlacementId')
        || `loot_placement_${index + 1}`,
    ),
    containerTypeId: String(
      read(value, 'containerTypeId', 'ContainerTypeId') || '',
    ),
    x: Number(read(value, 'x', 'X')) || 0,
    y: Number(read(value, 'y', 'Y')) || 0,
  };
}

export function normalizeLootContainerSpawnRule(value, index = 0) {
  const nearValue = read(value, 'near', 'Near');
  const near = nearValue || {};
  return {
    key: String(
      read(value, 'key', 'Key') || `loot_rule_${index + 1}`,
    ),
    containerTypeId: String(
      read(value, 'containerTypeId', 'ContainerTypeId') || '',
    ),
    count: Math.max(1, Number(read(value, 'count', 'Count')) || 1),
    allowedBaseKeys: stringList(
      read(value, 'allowedBaseKeys', 'AllowedBaseKeys'),
    ),
    allowedDetailKeysAny: stringList(
      read(value, 'allowedDetailKeysAny', 'AllowedDetailKeysAny'),
    ),
    near: nearValue == null
      ? null
      : {
        baseKeys: stringList(read(near, 'baseKeys', 'BaseKeys')),
        detailKeysAny: stringList(
          read(near, 'detailKeysAny', 'DetailKeysAny'),
        ),
        maxManhattanDistance: Math.max(
          1,
          Number(
            read(near, 'maxManhattanDistance', 'MaxManhattanDistance'),
          ) || 1,
        ),
      },
    minimumSpacing: Math.max(
      0,
      Number(read(value, 'minimumSpacing', 'MinimumSpacing')) || 0,
    ),
    excludeSpawnRadius: Math.max(
      0,
      Number(read(value, 'excludeSpawnRadius', 'ExcludeSpawnRadius')) || 0,
    ),
    enabled: read(value, 'enabled', 'Enabled') !== false,
  };
}

export function ensureRegionMap(shape, width, height) {
  const resolvedWidth = Math.max(
    1,
    Number(width ?? read(shape, 'width', 'Width')) || 1,
  );
  const resolvedHeight = Math.max(
    1,
    Number(height ?? read(shape, 'height', 'Height')) || 1,
  );
  const sourceRows = read(shape, 'tiles', 'Tiles');
  if (!shape || !Array.isArray(sourceRows)) {
    const fallback = createDefaultMap(resolvedWidth, resolvedHeight);
    return {
      ...fallback,
      id: '',
      name: '',
      regionId: '',
      revision: 0,
      spawnLocationX: 0,
      spawnLocationY: 0,
      campEntranceX: null,
      campEntranceY: null,
      championPlacement: null,
      exits: [],
      resourceNodes: [],
      resourceSpawnRules: [],
      lootContainerPlacements: [],
      lootContainerSpawnRules: [],
    };
  }

  const tiles = [];
  for (let y = 0; y < resolvedHeight; y += 1) {
    const row = [];
    for (let x = 0; x < resolvedWidth; x += 1) {
      const current = sourceRows[y]?.[x];
      row.push({
        base: current?.base ?? current?.Base ?? 'sand',
        detail: current?.detail ?? current?.Detail ?? null,
        walkableOverride:
          current?.walkableOverride ?? current?.WalkableOverride ?? null,
      });
    }
    tiles.push(row);
  }

  const exits = read(shape, 'exits', 'Exits');
  const championPlacement = read(
    shape,
    'championPlacement',
    'ChampionPlacement',
  );
  const resourceNodes = read(shape, 'resourceNodes', 'ResourceNodes');
  const rules = read(
    shape,
    'resourceSpawnRules',
    'ResourceSpawnRules',
  );
  const lootPlacements = read(
    shape,
    'lootContainerPlacements',
    'LootContainerPlacements',
  );
  const lootRules = read(
    shape,
    'lootContainerSpawnRules',
    'LootContainerSpawnRules',
  );
  return {
    id: String(read(shape, 'id', 'Id') || ''),
    name: String(read(shape, 'name', 'Name') || ''),
    regionId: String(read(shape, 'regionId', 'RegionId') || ''),
    width: resolvedWidth,
    height: resolvedHeight,
    revision: Math.max(0, Number(read(shape, 'revision', 'Revision')) || 0),
    spawnLocationX: Number(
      read(shape, 'spawnLocationX', 'SpawnLocationX'),
    ) || 0,
    spawnLocationY: Number(
      read(shape, 'spawnLocationY', 'SpawnLocationY'),
    ) || 0,
    campEntranceX: integerOrNull(
      read(shape, 'campEntranceX', 'CampEntranceX'),
    ),
    campEntranceY: integerOrNull(
      read(shape, 'campEntranceY', 'CampEntranceY'),
    ),
    tiles,
    championPlacement: normalizeFixedEncounter(championPlacement),
    exits: (Array.isArray(exits) ? exits : [])
      .map(normalizeRegionExit),
    resourceNodes: (Array.isArray(resourceNodes) ? resourceNodes : [])
      .map(normalizeResourcePlacement),
    resourceSpawnRules: (Array.isArray(rules) ? rules : [])
      .map(normalizeResourceSpawnRule),
    lootContainerPlacements: (
      Array.isArray(lootPlacements) ? lootPlacements : []
    ).map(normalizeLootContainerPlacement),
    lootContainerSpawnRules: (
      Array.isArray(lootRules) ? lootRules : []
    ).map(normalizeLootContainerSpawnRule),
  };
}

export function nextMapEntryKey(prefix, entries) {
  const keys = new Set((entries || []).map(entry => entry.key));
  let ordinal = 1;
  while (keys.has(`${prefix}_${ordinal}`)) ordinal += 1;
  return `${prefix}_${ordinal}`;
}

export function resizeMap(shape, width, height) {
  const next = ensureRegionMap(shape, width, height);
  const clamp = (value, maximum) => Math.min(
    Math.max(0, Number(value) || 0),
    Math.max(0, maximum - 1),
  );
  return {
    ...next,
    spawnLocationX: clamp(next.spawnLocationX, next.width),
    spawnLocationY: clamp(next.spawnLocationY, next.height),
    campEntranceX: next.campEntranceX === null
      ? null
      : clamp(next.campEntranceX, next.width),
    campEntranceY: next.campEntranceY === null
      ? null
      : clamp(next.campEntranceY, next.height),
    championPlacement: next.championPlacement == null
      ? null
      : {
        ...next.championPlacement,
        x: clamp(next.championPlacement.x, next.width),
        y: clamp(next.championPlacement.y, next.height),
      },
  };
}

export function resizeMapEdge(shape, edge, amount) {
  const current = ensureRegionMap(shape);
  const changesWidth = edge === 'left' || edge === 'right';
  const changesHeight = edge === 'top' || edge === 'bottom';
  const currentSize = changesWidth ? current.width : current.height;
  if (!['top', 'right', 'bottom', 'left'].includes(edge)
    || ![1, -1].includes(amount)
    || (amount === 1 && currentSize >= 200)
    || (amount === -1 && currentSize <= 1)) {
    return current;
  }

  const xOffset = edge === 'left' ? amount : 0;
  const yOffset = edge === 'top' ? amount : 0;
  const next = resizeMap(
    current,
    current.width + (changesWidth ? amount : 0),
    current.height + (changesHeight ? amount : 0),
  );
  const moveCoordinate = (value, offset, maximum) => Math.min(
    Math.max(0, value + offset),
    maximum - 1,
  );
  const shift = entry => ({
    ...entry,
    x: moveCoordinate(entry.x, xOffset, next.width),
    y: moveCoordinate(entry.y, yOffset, next.height),
  });
  const cloneTile = tile => ({ ...tile });
  let tiles;
  if (edge === 'top') {
    tiles = amount === 1
      ? [current.tiles[0].map(cloneTile), ...current.tiles]
      : current.tiles.slice(1);
  } else if (edge === 'bottom') {
    tiles = amount === 1
      ? [...current.tiles, current.tiles[current.height - 1].map(cloneTile)]
      : current.tiles.slice(0, -1);
  } else if (edge === 'left') {
    tiles = current.tiles.map(row => (
      amount === 1 ? [cloneTile(row[0]), ...row] : row.slice(1)
    ));
  } else {
    tiles = current.tiles.map(row => (
      amount === 1
        ? [...row, cloneTile(row[current.width - 1])]
        : row.slice(0, -1)
    ));
  }

  const spawn = shift({
    x: current.spawnLocationX,
    y: current.spawnLocationY,
  });

  return {
    ...next,
    tiles,
    spawnLocationX: spawn.x,
    spawnLocationY: spawn.y,
    campEntranceX: current.campEntranceX === null
      ? null
      : moveCoordinate(current.campEntranceX, xOffset, next.width),
    campEntranceY: current.campEntranceY === null
      ? null
      : moveCoordinate(current.campEntranceY, yOffset, next.height),
    exits: current.exits.map(shift),
    resourceNodes: current.resourceNodes.map(shift),
    lootContainerPlacements: current.lootContainerPlacements.map(shift),
    championPlacement: current.championPlacement == null
      ? null
      : shift(current.championPlacement),
  };
}

export function mergeSavedMapVersion(map, saved) {
  if (!saved) return map;
  return {
    ...map,
    id: saved.id ?? saved.Id ?? map.id,
    name: saved.name ?? saved.Name ?? map.name,
    regionId: saved.regionId ?? saved.RegionId ?? map.regionId,
    revision: saved.revision ?? saved.Revision ?? map.revision,
  };
}

export function mapPayload(map) {
  return {
    id: map.id,
    name: map.name,
    regionId: map.regionId,
    width: map.width,
    height: map.height,
    revision: map.revision,
    expectedRevision: map.revision,
    spawnLocationX: map.spawnLocationX,
    spawnLocationY: map.spawnLocationY,
    campEntranceX: map.campEntranceX,
    campEntranceY: map.campEntranceY,
    championPlacement: map.championPlacement,
    tiles: map.tiles,
    exits: map.exits,
    resourceNodes: map.resourceNodes,
    resourceSpawnRules: map.resourceSpawnRules,
    lootContainerPlacements: map.lootContainerPlacements,
    lootContainerSpawnRules: map.lootContainerSpawnRules,
  };
}
